import type { PanelBlock, PanelRender, Tier } from './types';
import { el, mountShell, shellHost, shellRoot } from './shell';
import { HostLayout, type LayoutMode } from './layout';
import { clamp } from './util';
import { log } from './logger';
import { consumeSelfNavigation, save, type ApprovalOutcome } from './persist';

interface PanelCallbacks {
  onUserMessage: (text: string) => void;
  onApproval: (requestId: string, approved: boolean) => void;
  onOpenChange: (open: boolean) => void;
}

interface PanelState {
  open: boolean;
  width: number;
  mode: LayoutMode;
}

const MAX_TRANSCRIPT = 60;
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

let callbacks: PanelCallbacks;
let layout: HostLayout;
let state: PanelState = { open: false, width: 400, mode: 'push' };

let launcher: HTMLButtonElement;
let panel: HTMLElement;
let stream: HTMLElement;
let emptyState: HTMLElement;
let input: HTMLTextAreaElement;
let sendButton: HTMLButtonElement;
let statusLine: HTMLElement;
let connectionLabel: HTMLElement;
let liveRegion: HTMLElement;

let approvalOutcomes: Record<string, ApprovalOutcome> = {};
let transcript: PanelBlock[] = [];
let replaying = false;

function announce(text: string): void {
  if (liveRegion) liveRegion.textContent = text;
}

function autoScroll(instant = false): void {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const behavior: ScrollBehavior = instant || reduced ? 'auto' : 'smooth';
  requestAnimationFrame(() => {
    stream.scrollTo({ top: stream.scrollHeight, behavior });
  });
}

function renderMessage(block: Extract<PanelBlock, { kind: 'message' }>): HTMLElement {
  const wrapper = el('div', 'block msg');
  wrapper.dataset.role = block.role;
  wrapper.appendChild(el('div', 'msg__bubble', block.text));
  return wrapper;
}

type SuggestionsBlock = Extract<PanelBlock, { kind: 'suggestions' }>;

let liveSuggestions: { block: SuggestionsBlock; wrapper: HTMLElement }[] = [];

/**
 * Suggestions belong to the question that raised them. Once the conversation
 * moves on — the user picks one, types their own message, or the agent asks
 * something new — the old buttons answer a question nobody is asking any more,
 * so retire every set except the newest. Being spent is recorded on the block
 * itself, so it travels with the saved transcript and survives a reload.
 */
function retireSuggestions(): void {
  const retiring = liveSuggestions.filter((entry) => !entry.block.spent);
  liveSuggestions = [];
  if (retiring.length === 0) return;

  for (const entry of retiring) {
    entry.block.spent = true;
    entry.wrapper.hidden = true;
  }
  save({ blocks: transcript });
}

function renderSuggestions(block: SuggestionsBlock): HTMLElement {
  const wrapper = el('div', 'block suggestions');
  if (block.spent) {
    wrapper.hidden = true;
    return wrapper;
  }

  retireSuggestions();

  for (const item of block.items) {
    const button = el('button', 'suggestion');
    button.type = 'button';
    button.appendChild(el('span', undefined, item));
    button.appendChild(el('span', undefined, '›'));
    button.addEventListener('click', () => {
      if (block.spent) return;
      submit(item);
    });
    wrapper.appendChild(button);
  }

  liveSuggestions.push({ block, wrapper });
  return wrapper;
}

function renderStatus(block: Extract<PanelBlock, { kind: 'status' }>): HTMLElement {
  const wrapper = el('div', 'block status');
  const dots = el('span', 'dots');
  dots.append(el('i'), el('i'), el('i'));
  wrapper.append(dots, el('span', undefined, block.text));
  return wrapper;
}

const TIER_COPY: Record<Tier, string> = {
  READ: 'Read only',
  WRITE: 'Changes your data',
  RED: 'High impact',
};

function renderApproval(block: Extract<PanelBlock, { kind: 'approval' }>): HTMLElement {
  const wrapper = el('div', 'block approval');
  wrapper.dataset.tier = block.tier;
  wrapper.setAttribute('role', 'group');
  wrapper.setAttribute('aria-label', 'Approval required');

  const head = el('div', 'approval__head');
  const badge = el('span', 'badge', TIER_COPY[block.tier]);
  badge.dataset.tier = block.tier;
  head.append(el('span', undefined, 'Approval required'), badge);

  const body = el('div', 'approval__body');
  body.appendChild(el('p', 'approval__desc', block.description || 'The assistant wants to act.'));

  const actions = el('div', 'approval__actions');
  const approve = el('button', 'btn');
  approve.type = 'button';
  approve.dataset.variant = block.tier === 'RED' ? 'danger' : 'primary';
  approve.textContent = block.tier === 'READ' ? 'Allow' : 'Approve';

  const decline = el('button', 'btn', 'Decline');
  decline.type = 'button';
  decline.dataset.variant = 'secondary';

  const resolve = (approved: boolean) => {
    if (approvalOutcomes[block.request_id]) return;
    approvalOutcomes[block.request_id] = approved ? 'approved' : 'declined';

    save({ approvals: approvalOutcomes });
    actions.remove();
    body.appendChild(
      el('p', 'approval__resolved', approved ? 'Approved by you' : 'Declined by you'),
    );
    announce(approved ? 'Approved' : 'Declined');
    callbacks.onApproval(block.request_id, approved);
  };

  approve.addEventListener('click', () => resolve(true));
  decline.addEventListener('click', () => resolve(false));

  actions.append(approve, decline);
  body.appendChild(actions);
  wrapper.append(head, body);

  const known = approvalOutcomes[block.request_id];
  if (known) {
    actions.remove();
    body.appendChild(
      el('p', 'approval__resolved', known === 'approved' ? 'Approved by you' : 'Declined by you'),
    );
  } else if (replaying) {
    actions.remove();
    body.appendChild(el('p', 'approval__resolved', 'Expired on page change'));
  }

  announce(`Approval required: ${block.description}`);
  return wrapper;
}

const RENDERERS: { [K in PanelBlock['kind']]: (block: PanelBlock & { kind: K }) => HTMLElement } = {
  message: renderMessage,
  suggestions: renderSuggestions,
  approval: renderApproval,
  status: renderStatus,
};

/**
 * The panel opens on its own, so it has to say something when it opens. Without
 * a transcript to replay and before the agent speaks, the body is otherwise a
 * blank sheet.
 */
function syncEmptyState(): void {
  emptyState.hidden = stream.querySelector('.block') !== null;
}

export function renderBlocks(render: PanelRender): void {
  if (!replaying) {
    const durable = render.blocks.filter((b) => b.kind !== 'status');
    if (durable.length > 0) {
      transcript = [...transcript, ...durable].slice(-MAX_TRANSCRIPT);
      save({ blocks: transcript });
    }
  }
  for (const block of render.blocks) {
    const renderer = RENDERERS[block.kind] as ((b: PanelBlock) => HTMLElement) | undefined;
    if (!renderer) {
      log.warn(`no renderer for block kind "${block.kind}"`);
      continue;
    }
    const node = renderer(block);
    // Restored history is not new: it should not animate in one block at a time,
    // and the stream should already be at the bottom rather than scrolling there.
    if (replaying) node.dataset.restored = 'true';
    stream.appendChild(node);
  }
  syncEmptyState();
  autoScroll(replaying);
}

export function appendAgentMessage(text: string): void {
  renderBlocks({ blocks: [{ kind: 'message', role: 'agent', text }] });
}

export function appendError(text: string): void {
  const wrapper = el('div', 'block status', text);
  wrapper.dataset.tone = 'error';
  wrapper.setAttribute('role', 'alert');
  stream.appendChild(wrapper);
  syncEmptyState();
  autoScroll();
  announce(text);
}

export function setStatus(text: string): void {
  statusLine.textContent = '';
  if (!text) {
    statusLine.hidden = true;
    return;
  }
  statusLine.hidden = false;
  const dots = el('span', 'dots');
  dots.append(el('i'), el('i'), el('i'));
  statusLine.append(dots, el('span', undefined, text));
  announce(text);
}

export function setConnectionState(value: 'connecting' | 'open' | 'closed'): void {
  const copy = { connecting: 'Connecting…', open: 'Connected', closed: 'Offline' };
  connectionLabel.textContent = copy[value];
  connectionLabel.dataset.state = value;
}

function submit(text: string): void {
  const trimmed = text.trim();
  if (!trimmed) return;
  retireSuggestions();
  renderBlocks({ blocks: [{ kind: 'message', role: 'user', text: trimmed }] });
  input.value = '';
  input.style.height = 'auto';
  sendButton.disabled = true;
  callbacks.onUserMessage(trimmed);
}

export function setOpen(open: boolean, moveFocus = true): void {
  if (open) setShellVisible(true);
  if (state.open === open) return;
  state.open = open;
  panel.dataset.open = String(open);
  panel.inert = !open;
  launcher.hidden = open;
  launcher.setAttribute('aria-expanded', String(open));

  state.mode = layout.apply(open, state.width);
  panel.dataset.mode = state.mode;

  if (!moveFocus) {
    save({ open });
    callbacks.onOpenChange(open);
    return;
  }
  if (open) requestAnimationFrame(() => input.focus());
  else launcher.focus();

  save({ open });
  callbacks.onOpenChange(open);
}

export function restoreTranscript(
  blocks: PanelBlock[],
  approvals: Record<string, ApprovalOutcome> = {},
): void {
  if (blocks.length === 0) return;
  replaying = true;
  transcript = blocks;
  approvalOutcomes = { ...approvals };
  renderBlocks({ blocks });
  replaying = false;
  if (!consumeSelfNavigation()) {
    const note = el('div', 'block status', 'Continued from the previous page');
    note.dataset.restored = 'true';
    stream.appendChild(note);
  }
  autoScroll(true);
}

function dismiss(): void {
  save({ dismissed: true });
  setOpen(false);
}

export function isOpen(): boolean {
  return state.open;
}

/**
 * Hold the whole surface back so the host application renders on its own for a
 * moment before the assistant appears. Display is set on the element rather
 * than through [hidden], whose user-agent rule loses to the author display in
 * our own stylesheet.
 */
export function setShellVisible(visible: boolean): void {
  const host = shellHost();
  if (!host) return;
  if (visible) host.style.removeProperty('display');
  else host.style.setProperty('display', 'none', 'important');
}

export function setWidth(width: number): void {
  state.width = clamp(Math.round(width), MIN_WIDTH, MAX_WIDTH);
  panel.style.width = `${state.width}px`;
  resizeHandle.setAttribute('aria-valuenow', String(state.width));
  save({ width: state.width });
  if (state.open) state.mode = layout.apply(true, state.width);
}

let resizeHandle: HTMLElement;

function buildResizeHandle(): HTMLElement {
  const handle = el('div', 'resize');
  handle.setAttribute('role', 'separator');
  handle.setAttribute('aria-orientation', 'vertical');
  handle.setAttribute('aria-label', 'Resize assistant panel');
  handle.setAttribute('aria-valuemin', String(MIN_WIDTH));
  handle.setAttribute('aria-valuemax', String(MAX_WIDTH));
  handle.tabIndex = 0;

  let pointerId: number | null = null;
  handle.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    pointerId = event.pointerId;
    handle.setPointerCapture(event.pointerId);
    handle.dataset.dragging = 'true';
  });
  handle.addEventListener('pointermove', (event) => {
    if (pointerId !== event.pointerId) return;
    setWidth(window.innerWidth - event.clientX);
  });
  const end = (event: PointerEvent) => {
    if (pointerId !== event.pointerId) return;
    handle.releasePointerCapture(event.pointerId);
    pointerId = null;
    delete handle.dataset.dragging;
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
  handle.addEventListener('keydown', (event) => {
    const step = 24;
    if (event.key === 'ArrowLeft') setWidth(state.width + step);
    else if (event.key === 'ArrowRight') setWidth(state.width - step);
    else if (event.key === 'Home') setWidth(MAX_WIDTH);
    else if (event.key === 'End') setWidth(MIN_WIDTH);
    else return;
    event.preventDefault();
  });

  return handle;
}

interface MountPanelOptions {
  title: string;
  theme: 'light' | 'dark' | 'system';
  mode: LayoutMode;
  hostSelector?: string;
  width: number;
  callbacks: PanelCallbacks;
}

export function mountPanel(options: MountPanelOptions): ShadowRoot {
  callbacks = options.callbacks;
  layout = new HostLayout(options.mode, options.hostSelector, () => shellHost());
  state = { open: false, width: options.width, mode: options.mode };

  const root = mountShell(options.theme);

  launcher = el('button', 'launcher');
  launcher.type = 'button';
  launcher.setAttribute('aria-expanded', 'false');
  launcher.setAttribute('aria-controls', 'agent-panel');
  launcher.append(el('span', undefined, '✦'), el('span', undefined, `Ask ${options.title}`));
  launcher.addEventListener('click', () => setOpen(true));

  panel = el('section', 'panel');
  panel.id = 'agent-panel';
  panel.style.width = `${state.width}px`;
  panel.dataset.open = 'false';
  panel.dataset.mode = options.mode;
  panel.inert = true;
  panel.setAttribute('role', 'complementary');
  panel.setAttribute('aria-label', `${options.title} assistant`);

  resizeHandle = buildResizeHandle();
  resizeHandle.setAttribute('aria-valuenow', String(state.width));

  const header = el('div', 'header');
  header.appendChild(el('span', 'header__mark', '✦'));
  const titles = el('div', 'header__titles');
  titles.appendChild(el('p', 'header__title', `${options.title} | AI Product Assistant`));
  connectionLabel = el('p', 'header__sub header__state', 'Connecting…');
  connectionLabel.dataset.state = 'connecting';
  titles.appendChild(connectionLabel);
  header.appendChild(titles);

  const close = el('button', 'iconbtn', '✕');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close assistant');
  close.addEventListener('click', () => dismiss());
  header.appendChild(close);

  stream = el('div', 'stream');
  stream.setAttribute('role', 'log');
  stream.setAttribute('aria-label', 'Conversation');
  stream.setAttribute('aria-relevant', 'additions');

  emptyState = el('div', 'empty');
  emptyState.append(
    el('span', 'empty__mark', '✦'),
    el('p', 'empty__title', `Ask ${options.title} about this screen`),
    el(
      'p',
      'empty__body',
      'I can explain what you are looking at, find things for you, and take actions on your behalf. Anything that changes your data waits for your approval.',
    ),
  );
  stream.appendChild(emptyState);

  statusLine = el('div', 'block status');
  statusLine.hidden = true;

  const composer = el('form', 'composer');
  const row = el('div', 'composer__row');
  const label = el('label', 'sr-only', 'Ask about this page');
  label.htmlFor = 'agent-input';
  input = el('textarea', 'composer__input');
  input.id = 'agent-input';
  input.rows = 1;
  input.placeholder = 'Ask about this page…';
  input.maxLength = 2000;

  sendButton = el('button', 'composer__send', '↑');
  sendButton.type = 'submit';
  sendButton.disabled = true;
  sendButton.setAttribute('aria-label', 'Send message');

  input.addEventListener('input', () => {
    sendButton.disabled = input.value.trim().length === 0;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 132)}px`;
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      submit(input.value);
    }
  });
  composer.addEventListener('submit', (event) => {
    event.preventDefault();
    submit(input.value);
  });

  row.append(label, input, sendButton);
  const hint = el('p', 'composer__hint');
  hint.appendChild(
    el('span', undefined, 'Alpha can make mistakes. Please verify important information.'),
  );
  composer.append(row, hint);

  liveRegion = el('p', 'sr-only');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');

  panel.append(resizeHandle, header, stream, statusLine, composer, liveRegion);
  root.append(launcher, panel);

  root.addEventListener('keydown', (event) => {
    if (event instanceof KeyboardEvent && event.key === 'Escape' && state.open) {
      event.stopPropagation();
      dismiss();
    }
  });

  return root;
}

export function destroyPanel(): void {
  layout?.destroy();
  approvalOutcomes = {};
  liveSuggestions = [];
  transcript = [];
  const root = shellRoot();
  if (root) root.textContent = '';
}
